import { getCurrentInstance, nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import "./bodyPortalDropdown.css";

export const BODY_PORTAL_Z_INDEX = 10001;
export const BODY_PORTAL_ATTRIBUTE = "data-body-portal-dropdown";
export const BODY_PORTAL_SELECTOR = `[${BODY_PORTAL_ATTRIBUTE}]`;
const DEFAULT_GAP_PX = 2;
// Open above the field when fewer than this many px remain below it and the list is not measurable yet.
const DEFAULT_FLIP_THRESHOLD_PX = 240;

/**
 * Shared body-portal for picker option lists (Dialog & Picker Overlay standard, ER-12221).
 *
 * Vue 2 has no Teleport, so the list node is moved to <body> while open: no dialog/card
 * scroll or clip box can trap it, and opening it never resizes its container. The node is
 * positioned `fixed` from the anchor's rect, flips above the anchor when there is no room
 * below, follows the anchor on scroll/resize, and closes (and is removed from the body) on
 * outside click, Esc, or when the container is hidden or destroyed.
 *
 * Imperative on purpose so Options API (`setup()`) and `<script setup>` pickers share one
 * implementation: call `attach()` when the list becomes visible and `detach()` when it hides.
 *
 * @param {object} options
 * @param {() => Element|null} options.getAnchor      field the list is anchored to (width follows it)
 * @param {() => Element|null} options.getDropdown    the list node rendered in the picker template
 * @param {() => Element|null} [options.getContainer] root whose clicks never count as "outside" (defaults to the anchor)
 * @param {() => void} options.onClose                called on outside click, Esc, or when the container is hidden
 * @param {number} [options.gap]                      px between anchor and list
 * @param {number} [options.flipThreshold]            px of room needed below before the list is measurable
 * @param {boolean} [options.closeOnOutsideClick]
 * @param {boolean} [options.closeOnEscape]
 */
export function useBodyPortalDropdown({
  getAnchor,
  getDropdown,
  getContainer = null,
  onClose,
  gap = DEFAULT_GAP_PX,
  flipThreshold = DEFAULT_FLIP_THRESHOLD_PX,
  closeOnOutsideClick = true,
  closeOnEscape = true,
}) {
  const dropdownStyle = ref({
    position: "fixed",
    zIndex: BODY_PORTAL_Z_INDEX,
    top: "0px",
    left: "0px",
  });

  let attached = false;
  let portalEl = null;
  let resizeObserver = null;

  const requestClose = () => {
    if (typeof onClose === "function") onClose();
  };

  const isInside = (target) => {
    if (!(target instanceof Node)) return false;
    const container = (getContainer || getAnchor)();
    const dropdown = getDropdown();
    return !!(container?.contains?.(target) || dropdown?.contains?.(target));
  };

  const updatePosition = () => {
    const anchor = getAnchor();
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const needed = (getDropdown()?.offsetHeight || flipThreshold) + gap;
    const openAbove = spaceBelow < needed && rect.top > spaceBelow;
    dropdownStyle.value = {
      position: "fixed",
      zIndex: BODY_PORTAL_Z_INDEX,
      left: `${Math.round(rect.left)}px`,
      width: `${Math.round(rect.width)}px`,
      top: openAbove ? "" : `${Math.round(rect.bottom + gap)}px`,
      bottom: openAbove ? `${Math.round(window.innerHeight - rect.top + gap)}px` : "",
    };
  };

  // Vuetify's v-dialog pulls focus back inside itself on any `focusin` outside its content;
  // the portaled list lives outside it, so keep focus moves inside the list from reaching window.
  const stopFocusIn = (event) => {
    event.stopPropagation();
  };

  const moveToBody = () => {
    const el = getDropdown();
    if (!el) return;
    portalEl = el;
    el.setAttribute(BODY_PORTAL_ATTRIBUTE, "");
    el.addEventListener("focusin", stopFocusIn);
    if (el.parentNode !== document.body) document.body.appendChild(el);
  };

  const onViewportScroll = (event) => {
    // Scrolling the list itself (pagination) must not re-anchor it.
    if (event?.target instanceof Node && getDropdown()?.contains?.(event.target)) return;
    updatePosition();
  };

  // A dialog still animating in (scale/translate) has a different rect than once it settles.
  const onAnimationEnd = (event) => {
    const anchor = getAnchor();
    if (event?.target instanceof Node && anchor && event.target.contains(anchor)) {
      updatePosition();
    }
  };

  const onDocumentClick = (event) => {
    if (!isInside(event.target)) requestClose();
  };

  const onDocumentKeydown = (event) => {
    if (event.key !== "Escape") return;
    // Close only the list; the surrounding dialog must stay open.
    event.stopPropagation();
    requestClose();
  };

  const observeAnchor = () => {
    const anchor = getAnchor();
    if (typeof ResizeObserver === "undefined" || !anchor) return;
    resizeObserver?.disconnect();
    resizeObserver = new ResizeObserver(() => {
      const rect = anchor.getBoundingClientRect();
      // A zero-size anchor means its container (dialog) was hidden, not destroyed.
      if (!rect.width && !rect.height) {
        requestClose();
      } else {
        updatePosition();
      }
    });
    resizeObserver.observe(anchor);
  };

  const removeListeners = () => {
    window.removeEventListener("scroll", onViewportScroll, true);
    window.removeEventListener("resize", updatePosition);
    window.removeEventListener("transitionend", onAnimationEnd, true);
    window.removeEventListener("animationend", onAnimationEnd, true);
    document.removeEventListener("click", onDocumentClick, true);
    document.removeEventListener("keydown", onDocumentKeydown, true);
    resizeObserver?.disconnect();
    resizeObserver = null;
    portalEl?.removeEventListener("focusin", stopFocusIn);
  };

  const removeFromBody = () => {
    const el = portalEl;
    portalEl = null;
    if (!el) return;
    el.removeAttribute(BODY_PORTAL_ATTRIBUTE);
    // Only unpark the node while it still lives in <body>. If a branch swap already
    // re-adopted it into the picker's own tree, removing it here would delete a live
    // element (the clear button, ER-12679).
    if (el.parentNode === document.body) document.body.removeChild(el);
  };

  const attach = () => {
    if (attached) return;
    attached = true;
    window.addEventListener("scroll", onViewportScroll, true);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("transitionend", onAnimationEnd, true);
    window.addEventListener("animationend", onAnimationEnd, true);
    if (closeOnOutsideClick) document.addEventListener("click", onDocumentClick, true);
    if (closeOnEscape) document.addEventListener("keydown", onDocumentKeydown, true);
    nextTick(() => {
      if (!attached) return;
      moveToBody();
      updatePosition();
      observeAnchor();
      // Wrapping at the new width can change the measured height.
      nextTick(() => attached && updatePosition());
    });
  };

  // Also drop the node from <body>: when the list sits inside a branch Vue swaps out as a whole
  // (v-if/v-else around the field) Vue only removes that branch's root, never the moved node.
  const detach = () => {
    attached = false;
    removeListeners();
    removeFromBody();
  };

  // Likewise when the picker is destroyed while its list is open (the dialog unmounts).
  const destroy = detach;

  if (getCurrentInstance()) onBeforeUnmount(destroy);

  return { dropdownStyle, attach, detach, destroy, updatePosition, isInside };
}

export default useBodyPortalDropdown;

/** True when the event originated inside a picker option list that was moved to <body>. */
export function isPortalDropdownEvent(event) {
  const target = event?.target;
  return target instanceof Element && !!target.closest(BODY_PORTAL_SELECTOR);
}

/**
 * Lets a temporary drawer tell a click on a portaled picker list from a real click outside it.
 * Vuetify closes the drawer on any click outside its element and only emits `input(false)` after a
 * timeout, by which time the clicked option has left <body> and the event target is no longer known.
 * Record the origin of every pointer gesture (capture phase) and read it once from the drawer's
 * `input` handler through `consumePortalPointer()`.
 */
export function useBodyPortalPointerTracker() {
  let lastPointerInPortal = false;

  const onPointer = (event) => {
    lastPointerInPortal = isPortalDropdownEvent(event);
  };

  const attach = () => {
    document.addEventListener("mousedown", onPointer, true);
    document.addEventListener("click", onPointer, true);
  };

  const detach = () => {
    document.removeEventListener("mousedown", onPointer, true);
    document.removeEventListener("click", onPointer, true);
  };

  const consumePortalPointer = () => {
    const wasInPortal = lastPointerInPortal;
    lastPointerInPortal = false;
    return wasInPortal;
  };

  if (getCurrentInstance()) {
    onMounted(attach);
    onBeforeUnmount(detach);
  }

  return { consumePortalPointer, attach, detach };
}
